// DESCRIPTION: Frontend booking widget — event hero entry, then a stepped wizard
// DESCRIPTION: (pick a time → details → payment → done) with a sticky summary, wired to REST + Stripe.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { addFilter, applyFilters, doAction } from '@wordpress/hooks';
import { loadStripe } from '@stripe/stripe-js';
import {
	Elements,
	PaymentElement,
	useStripe,
	useElements,
} from '@stripe/react-stripe-js';

import DatePicker from './booking/DatePicker';
import SlotPicker from './booking/SlotPicker';
import AttendeeForm from './booking/AttendeeForm';
import StepIndicator from './booking/StepIndicator';
import BookingSummary from './booking/BookingSummary';
import EventHero from './booking/EventHero';
import Confirmation from './booking/Confirmation';
import WaitlistForm from './booking/WaitlistForm';
import { formatDateLong, formatMoney } from './booking/helpers';
import { formatTime } from './calendar/utils';
import { getEventStyleVars } from '../frontend/utils/eventStyling';

/**
 * Check if an event type uses slot-based scheduling.
 *
 * @param {string} eventType The event type slug.
 *
 * @return {boolean} Whether the event type uses date/slot scheduling.
 */
function isSlotBased( eventType ) {
	return eventType === 'one-on-one' || eventType === 'group';
}

/**
 * Form keys handled natively by AttendeeForm/BookingForm. Any other key
 * present on `form` is treated as a developer-injected custom field and
 * copied into `booking_meta.custom_fields` on submit.
 *
 * @type {string[]}
 */
const BUILT_IN_FORM_KEYS = [
	'firstName',
	'lastName',
	'email',
	'phone',
	'notes',
	'reminder',
	'terms',
];

/**
 * Built-in detail fields in visual order, paired with their input element ids.
 * Used to move focus to the first field that fails validation so the user sees
 * why the form didn't advance.
 *
 * @type {Array<[string, string]>}
 */
const DETAIL_FIELD_IDS = [
	[ 'firstName', 'nb-first-name' ],
	[ 'lastName', 'nb-last-name' ],
	[ 'email', 'nb-email' ],
	[ 'phone', 'nb-phone' ],
	[ 'terms', 'nb-terms' ],
];

addFilter(
	'nemtly_booking.booking.payload',
	'nemtly-booking/custom-fields',
	( payload, ctx ) => {
		const customFields = {};
		Object.keys( ctx.form || {} ).forEach( ( key ) => {
			if ( ! BUILT_IN_FORM_KEYS.includes( key ) ) {
				customFields[ key ] = ctx.form[ key ];
			}
		} );

		if ( Object.keys( customFields ).length === 0 ) {
			return payload;
		}

		return {
			...payload,
			booking_meta: {
				...payload.booking_meta,
				custom_fields: {
					...( payload.booking_meta?.custom_fields || {} ),
					...customFields,
				},
			},
		};
	}
);

/**
 * Stripe payment step body. Lives inside <Elements> so it can access the
 * Stripe instance; registers its confirm handler upward so the wizard footer
 * button can drive payment confirmation.
 *
 * @param {Object}   props
 * @param {Function} props.registerConfirm Receives the async confirm function.
 * @param {Function} props.onProcessing    Toggles the processing/loading state.
 * @param {Function} props.onPaid          Called after a successful payment.
 * @param {Function} props.onError         Called with an error message on failure.
 *
 * @return {JSX.Element} The Stripe payment fields.
 */
const PaymentStep = ( { registerConfirm, onProcessing, onPaid, onError } ) => {
	const stripe = useStripe();
	const elements = useElements();

	useEffect( () => {
		registerConfirm( async () => {
			if ( ! stripe || ! elements ) {
				return;
			}

			onProcessing( true );

			try {
				const { error } = await stripe.confirmPayment( {
					elements,
					confirmParams: { return_url: window.location.href },
					redirect: 'if_required',
				} );

				if ( error ) {
					onError( error.message );
					onProcessing( false );
				} else {
					onPaid();
				}
			} catch ( e ) {
				onError( e.message );
				onProcessing( false );
			}
		} );
	}, [ stripe, elements ] ); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="nb-stripe-frame">
			<PaymentElement />
			<div className="nb-stripe-note">
				🔒{ ' ' }
				{ __(
					'Payments are processed securely by Stripe. Your card details are never stored.',
					'nemtly-booking'
				) }
			</div>
		</div>
	);
};

/**
 * BookingForm — the public booking widget.
 *
 * @param {Object}   props
 * @param {number}   props.eventId          The event post ID to book.
 * @param {Object}   [props.initialEvent]   Server-rendered event seed. When
 *                                          provided, the hero paints instantly
 *                                          from this data and the event GET is
 *                                          skipped (only availability and
 *                                          payment methods are fetched).
 * @param {Function} props.onSuccess        Called after a successful booking/payment.
 * @param {Function} props.onError          Called when booking/payment fails.
 * @param {string}   [props.className='']   Additional CSS class names for the wrapper.
 * @param {boolean}  [props.showEntry=true] Show the event hero entry screen first.
 *                                          Set false when the host (e.g. a modal)
 *                                          already provides the entry context.
 *
 * @return {JSX.Element} The booking widget.
 */
const BookingForm = ( {
	eventId,
	initialEvent,
	onSuccess,
	onError,
	className = '',
	showEntry = true,
} ) => {
	// Screens: 'entry' (hero) → 'booking' (wizard, incl. confirmation as last step).
	const [ screen, setScreen ] = useState( showEntry ? 'entry' : 'booking' );
	const [ step, setStep ] = useState( 0 );

	// Event data — seeded by the server when available so the hero paints
	// without a loading flash or an extra network round-trip.
	const [ event, setEvent ] = useState( initialEvent || null );
	const [ availability, setAvailability ] = useState( null );
	const [ loadError, setLoadError ] = useState( '' );

	// Slot-based selection.
	const [ bookableDates, setBookableDates ] = useState( [] );
	const [ datesLoading, setDatesLoading ] = useState( false );
	const [ selectedDate, setSelectedDate ] = useState( '' );
	const [ slots, setSlots ] = useState( [] );
	const [ slotsLoading, setSlotsLoading ] = useState( false );
	const [ selectedSlot, setSelectedSlot ] = useState( null );

	// Attendee form.
	const [ form, setForm ] = useState( { reminder: true, terms: false } );
	const [ errors, setErrors ] = useState( {} );
	const [ isLoading, setIsLoading ] = useState( false );

	// Payment + result.
	const [ paymentMethods, setPaymentMethods ] = useState( null );
	const [ paymentMethod, setPaymentMethod ] = useState( 'stripe' );
	const [ clientSecret, setClientSecret ] = useState( '' );
	const [ stripePromise, setStripePromise ] = useState( null );
	const [ bookingResult, setBookingResult ] = useState( null );
	const [ successVariant, setSuccessVariant ] = useState( 'confirmed' );
	const [ manualInstructions, setManualInstructions ] = useState( '' );

	const panelRef = useRef( null );
	const paymentConfirmRef = useRef( null );

	// -- Data loading --

	useEffect( () => {
		if ( ! eventId ) {
			return;
		}

		// When the server seeded the event data, trust the seed for display
		// and only fetch the dynamic bits (availability, payment methods).
		// Skipping the event GET also avoids overwriting the seed with the
		// REST single-event shape, which uses different keys than the
		// components consume.
		if ( initialEvent ) {
			checkAvailability();
			if ( ( initialEvent.price || 0 ) > 0 ) {
				loadPaymentMethods();
			}
			return;
		}

		loadEventDetails();
	}, [ eventId ] ); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect( () => {
		if ( event && isSlotBased( event.event_type ) ) {
			loadBookableDates();
		}
	}, [ event ] ); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect( () => {
		if ( selectedDate && event && isSlotBased( event.event_type ) ) {
			loadSlots( selectedDate );
		}
	}, [ selectedDate ] ); // eslint-disable-line react-hooks/exhaustive-deps

	const loadEventDetails = async () => {
		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }`,
			} );

			if ( response.success ) {
				setEvent( response.result );
				checkAvailability();

				const ev = response.result;
				if ( ev && ( ev.price || 0 ) > 0 ) {
					loadPaymentMethods();
				}
			} else {
				setLoadError(
					__( 'Failed to load this event.', 'nemtly-booking' )
				);
			}
		} catch ( error ) {
			setLoadError(
				__( 'Failed to load this event.', 'nemtly-booking' )
			);
			if ( onError ) {
				onError(
					__( 'Failed to load event details', 'nemtly-booking' )
				);
			}
		}
	};

	const checkAvailability = async () => {
		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }/availability`,
			} );
			if ( response.success ) {
				setAvailability( response.result );
			}
		} catch ( error ) {
			// Silently fail — the form still renders.
		}
	};

	const loadPaymentMethods = async () => {
		try {
			const response = await apiFetch( {
				path: '/nemtly-booking/v1/payment-methods',
			} );
			if ( response.success && response.data ) {
				setPaymentMethods( response.data );
				if (
					response.data.accept_stripe === false &&
					response.data.accept_manual_payment
				) {
					setPaymentMethod( 'manual' );
				}
			}
		} catch ( err ) {
			setPaymentMethods( {
				accept_stripe: true,
				accept_manual_payment: false,
			} );
		}
	};

	const loadBookableDates = async () => {
		setDatesLoading( true );
		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }/dates?limit=60`,
			} );
			if ( response.success ) {
				const dates = response.result?.dates || response.result || [];
				setBookableDates( Array.isArray( dates ) ? dates : [] );
			}
		} catch ( error ) {
			// Silently fail — dates remain empty.
		} finally {
			setDatesLoading( false );
		}
	};

	const loadSlots = async ( date ) => {
		setSlotsLoading( true );
		setSlots( [] );
		setSelectedSlot( null );

		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }/slots?date=${ date }`,
			} );
			if ( response.success ) {
				const slotData =
					response.result?.slots || response.result || [];
				setSlots( Array.isArray( slotData ) ? slotData : [] );
			}
		} catch ( error ) {
			// Silently fail.
		} finally {
			setSlotsLoading( false );
		}
	};

	// -- Derived flags --

	const slotBased = event ? isSlotBased( event.event_type ) : false;
	const isPaid = event ? ( event.price || 0 ) > 0 : false;
	const acceptStripe =
		! paymentMethods || paymentMethods.accept_stripe !== false;
	const acceptManual = !! (
		paymentMethods && paymentMethods.accept_manual_payment
	);
	const showPayMethodPicker = isPaid && acceptStripe && acceptManual;
	let effectiveMethod = 'stripe';
	if ( isPaid && ! acceptStripe && acceptManual ) {
		effectiveMethod = 'manual';
	} else if ( showPayMethodPicker ) {
		effectiveMethod = paymentMethod;
	}
	const hasPaymentStep =
		isPaid && acceptStripe && effectiveMethod === 'stripe';

	// Context object passed to JS extensibility hooks (`nemtly_booking.booking.*`).
	const hookContext = {
		eventId,
		event,
		form,
		slotBased,
		isPaid,
		hasPaymentStep,
		step,
	};

	// Active step keys + labels.
	const baseStepKeys = [];
	if ( slotBased ) {
		baseStepKeys.push( 'time' );
	}
	baseStepKeys.push( 'details' );
	if ( hasPaymentStep ) {
		baseStepKeys.push( 'payment' );
	}
	baseStepKeys.push( 'done' );

	/**
	 * Filters the ordered list of booking wizard step keys.
	 *
	 * `details` and `done` must remain present, with `done` last. Unknown
	 * step keys render an empty panel unless populated via the
	 * `nemtly_booking.booking.step_render` filter.
	 *
	 * @param {string[]} stepKeys Step keys in display order.
	 * @param {Object}   ctx      Booking context.
	 */
	const stepKeys = applyFilters(
		'nemtly_booking.booking.steps',
		baseStepKeys,
		hookContext
	);

	const baseStepLabelMap = {
		time: __( 'Pick a time', 'nemtly-booking' ),
		details: __( 'Your details', 'nemtly-booking' ),
		payment: __( 'Payment', 'nemtly-booking' ),
		done: __( 'Done', 'nemtly-booking' ),
	};

	/**
	 * Filters the map of step key => step indicator label.
	 *
	 * @param {Object} stepLabelMap Map of step key to label.
	 * @param {Object} ctx          Booking context.
	 */
	const stepLabelMap = applyFilters(
		'nemtly_booking.booking.step_labels',
		baseStepLabelMap,
		hookContext
	);
	const stepLabels = stepKeys.map( ( k ) => stepLabelMap[ k ] );
	const currentKey = stepKeys[ step ] || stepKeys[ 0 ];
	const attendeeName =
		form.firstName && form.lastName
			? `${ form.firstName } ${ form.lastName }`.trim()
			: '';

	// -- Navigation helpers --

	const goToKey = ( key ) => {
		const idx = stepKeys.indexOf( key );
		if ( idx !== -1 ) {
			animateStep( () => {
				setStep( idx );

				/**
				 * Fires after the booking wizard navigates to a new step.
				 *
				 * @param {string} key   The step key navigated to.
				 * @param {number} index The step index navigated to.
				 * @param {Object} ctx   Booking context.
				 */
				doAction(
					'nemtly_booking.booking.step_changed',
					key,
					idx,
					hookContext
				);
			} );
		}
	};

	function animateStep( fn ) {
		const node = panelRef.current;
		if ( node ) {
			node.classList.add( 'nb-step-panel--exit' );
			setTimeout( () => {
				node.classList.remove( 'nb-step-panel--exit' );
				fn();
			}, 120 );
		} else {
			fn();
		}
	}

	const registerConfirm = useCallback( ( fn ) => {
		paymentConfirmRef.current = fn;
	}, [] );

	const resetAll = () => {
		setScreen( showEntry ? 'entry' : 'booking' );
		setStep( 0 );
		setSelectedDate( '' );
		setSelectedSlot( null );
		setForm( { reminder: true, terms: false } );
		setErrors( {} );
		setClientSecret( '' );
		setStripePromise( null );
		setBookingResult( null );
		setManualInstructions( '' );
		setSuccessVariant( 'confirmed' );
		setIsLoading( false );
	};

	// -- Validation --

	/**
	 * Move focus to the first detail field with a validation error, scoped to
	 * the active step panel so it works even with multiple widgets on a page.
	 *
	 * @param {Object} errs Map of field key to error message.
	 */
	const focusFirstError = ( errs ) => {
		const root = panelRef.current;
		if ( ! root ) {
			return;
		}
		for ( const [ key, id ] of DETAIL_FIELD_IDS ) {
			if ( errs[ key ] ) {
				const el = root.querySelector( `#${ id }` );
				if ( el && typeof el.focus === 'function' ) {
					el.focus();
				}
				return;
			}
		}
	};

	const validateDetails = () => {
		const e = {};
		if ( ! form.firstName?.trim() ) {
			e.firstName = __(
				'Please enter your first name',
				'nemtly-booking'
			);
		}
		if ( ! form.lastName?.trim() ) {
			e.lastName = __( 'Please enter your last name', 'nemtly-booking' );
		}
		if ( ! form.email?.trim() ) {
			e.email = __( 'Please enter your email address', 'nemtly-booking' );
		} else if ( ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( form.email ) ) {
			e.email = __(
				'Please enter a valid email address',
				'nemtly-booking'
			);
		}
		if (
			form.phone &&
			! /^[+]?[1-9][\d]{0,15}$/.test(
				form.phone.replace( /[\s\-()]/g, '' )
			)
		) {
			e.phone = __(
				'Please enter a valid phone number',
				'nemtly-booking'
			);
		}
		if ( ! form.terms ) {
			e.terms = __(
				'You must agree to the terms to continue',
				'nemtly-booking'
			);
		}

		/**
		 * Filters the validation errors for the attendee details step.
		 *
		 * @param {Object} errors Map of field key to error message.
		 * @param {Object} ctx    Booking context.
		 */
		const filteredErrors = applyFilters(
			'nemtly_booking.booking.validate',
			e,
			hookContext
		);
		setErrors( filteredErrors );

		const isValid = Object.keys( filteredErrors ).length === 0;
		if ( ! isValid ) {
			focusFirstError( filteredErrors );
		}
		return isValid;
	};

	// -- Booking submission (from the details step) --

	const submitBooking = async () => {
		setIsLoading( true );
		setErrors( {} );

		try {
			const payload = {
				event_id: eventId,
				attendee_name: attendeeName,
				attendee_email: form.email,
				attendee_phone: form.phone || '',
				booking_meta: {
					reminder: form.reminder !== false,
					...( form.notes ? { notes: form.notes } : {} ),
				},
			};

			if ( slotBased && selectedSlot ) {
				payload.booking_date = selectedDate;
				payload.booking_time = selectedSlot.start;
				payload.booking_end_time = selectedSlot.end;
			}

			if ( isPaid && paymentMethods ) {
				if ( acceptStripe && acceptManual ) {
					payload.payment_method = paymentMethod;
				} else if ( acceptManual ) {
					payload.payment_method = 'manual';
				} else {
					payload.payment_method = 'stripe';
				}
			}

			/**
			 * Filters the booking submission payload before it is sent to
			 * the REST API. Inject custom field values under
			 * `booking_meta.custom_fields`.
			 *
			 * @param {Object} payload The booking payload.
			 * @param {Object} ctx     Booking context.
			 */
			const finalPayload = applyFilters(
				'nemtly_booking.booking.payload',
				payload,
				hookContext
			);

			/**
			 * Fires before the booking submission request is sent.
			 *
			 * @param {Object} payload The booking payload.
			 * @param {Object} ctx     Booking context.
			 */
			doAction(
				'nemtly_booking.booking.before_submit',
				finalPayload,
				hookContext
			);

			const response = await apiFetch( {
				path: '/nemtly-booking/v1/bookings',
				method: 'POST',
				data: finalPayload,
			} );

			if ( ! response.success ) {
				throw new Error(
					response.message || __( 'Booking failed', 'nemtly-booking' )
				);
			}

			const result = response.data || response.result;
			setBookingResult( result );

			/**
			 * Fires after a successful booking submission response, before
			 * the wizard branches on payment/manual/confirmed outcomes.
			 *
			 * @param {Object} result The booking result returned by the API.
			 * @param {Object} ctx    Booking context.
			 */
			doAction(
				'nemtly_booking.booking.after_submit',
				result,
				hookContext
			);

			if (
				result.requires_payment &&
				result.client_secret &&
				result.stripe_pk
			) {
				setStripePromise( loadStripe( result.stripe_pk ) );
				setClientSecret( result.client_secret );
				setIsLoading( false );
				goToKey( 'payment' );
			} else if ( result.payment_method === 'manual' ) {
				setManualInstructions( result.manual_instructions || '' );
				setSuccessVariant( 'manual' );
				setIsLoading( false );
				if ( onSuccess ) {
					onSuccess( result, response.message );
				}
				goToKey( 'done' );
			} else if ( result.payment_pending ) {
				setSuccessVariant( 'payment_pending' );
				setIsLoading( false );
				if ( onSuccess ) {
					onSuccess( result, result.message || response.message );
				}
				goToKey( 'done' );
			} else {
				setSuccessVariant( 'confirmed' );
				setIsLoading( false );
				if ( onSuccess ) {
					onSuccess( result, response.message );
				}
				goToKey( 'done' );
			}
		} catch ( error ) {
			const message =
				error.message ||
				__(
					'Something went wrong. Please try again.',
					'nemtly-booking'
				);
			setErrors( { submit: message } );
			setIsLoading( false );
			if ( onError ) {
				onError( message );
			}
		}
	};

	const handlePaid = () => {
		setSuccessVariant( 'confirmed' );
		setIsLoading( false );
		if ( onSuccess ) {
			onSuccess(
				bookingResult || {},
				__( 'Payment successful! Booking confirmed.', 'nemtly-booking' )
			);
		}
		goToKey( 'done' );
	};

	// -- Footer actions --

	const handleNext = () => {
		if ( currentKey === 'time' ) {
			if ( ! selectedDate || ! selectedSlot ) {
				return;
			}
			goToKey( 'details' );
			return;
		}

		if ( currentKey === 'details' ) {
			if ( ! validateDetails() ) {
				return;
			}
			submitBooking();
			return;
		}

		if ( currentKey === 'payment' ) {
			if ( paymentConfirmRef.current ) {
				paymentConfirmRef.current();
			}
		}
	};

	const handleBack = () => {
		setErrors( {} );

		// Stepping back off the payment step discards the unpaid booking so a
		// re-submit creates a fresh one (rather than reusing a stale intent).
		if ( currentKey === 'payment' ) {
			setClientSecret( '' );
			setStripePromise( null );
			setBookingResult( null );
		}

		animateStep( () => {
			setStep( ( s ) => {
				const idx = Math.max( s - 1, 0 );
				doAction(
					'nemtly_booking.booking.step_changed',
					stepKeys[ idx ],
					idx,
					hookContext
				);
				return idx;
			} );
		} );
	};

	// -- Render: loading / error --

	if ( loadError ) {
		return (
			<div className={ `nb-app ${ className }`.trim() }>
				<div className="nb-app__error">
					<svg
						className="nb-app__error-icon"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
						/>
					</svg>
					<p>{ loadError }</p>
				</div>
			</div>
		);
	}

	if ( ! event ) {
		return (
			<div className={ `nb-app ${ className }`.trim() }>
				<div className="nb-loading">
					{ __( 'Loading booking form…', 'nemtly-booking' ) }
				</div>
			</div>
		);
	}

	// -- Render: unavailable (full) — applies in standalone and modal modes --

	const unavailable = availability && availability.is_available === false;
	if ( unavailable ) {
		return (
			<div
				className={ `nb-app ${ className }`.trim() }
				style={ getEventStyleVars( event ) }
			>
				{ event && event.allow_waitlist ? (
					<WaitlistForm eventId={ eventId } event={ event } />
				) : (
					<div className="nb-app__error">
						<p>
							{ availability.message ||
								__(
									'This event is currently full.',
									'nemtly-booking'
								) }
						</p>
					</div>
				) }
			</div>
		);
	}

	// -- Render: entry screen --

	if ( screen === 'entry' ) {
		return (
			<div
				className={ `nb-app ${ className }`.trim() }
				style={ getEventStyleVars( event ) }
			>
				<EventHero
					event={ event }
					availability={ availability }
					slotBased={ slotBased }
					onBook={ () => {
						setScreen( 'booking' );
						setStep( 0 );
					} }
					style={ getEventStyleVars( event ) }
				/>
			</div>
		);
	}

	// -- Render: booking wizard --

	const symbol = event.currency_symbol || event.currency || '';
	const orderDate = slotBased ? formatDateLong( selectedDate ) : event.date;
	const orderSlotRange = selectedSlot
		? `${ formatTime( selectedSlot.start ) } – ${ formatTime(
				selectedSlot.end
		  ) }`
		: '';
	const orderTime = slotBased ? orderSlotRange : event.time;

	const processingLabel = __( 'Processing…', 'nemtly-booking' );
	let nextLabel = __( 'Continue', 'nemtly-booking' );
	if ( currentKey === 'payment' ) {
		nextLabel = isLoading
			? processingLabel
			: __( 'Confirm & pay', 'nemtly-booking' );
	} else if ( currentKey === 'details' ) {
		if ( isLoading ) {
			nextLabel = processingLabel;
		} else if ( hasPaymentStep ) {
			nextLabel = __( 'Continue to payment', 'nemtly-booking' );
		} else {
			nextLabel = __( 'Confirm booking', 'nemtly-booking' );
		}
	}

	const nextDisabled =
		isLoading ||
		( currentKey === 'time' && ( ! selectedDate || ! selectedSlot ) );
	const isDone = currentKey === 'done';

	return (
		<div
			className={ `nb-app ${ className }`.trim() }
			style={ getEventStyleVars( event ) }
		>
			{ ! isDone && showEntry && (
				<div className="nb-app__bar">
					<button
						type="button"
						className="nemtly-button-ghost nemtly-button-sm"
						onClick={ resetAll }
					>
						{ __( '← Back to event', 'nemtly-booking' ) }
					</button>
				</div>
			) }

			<div className="nb-booking-shell">
				<div className="nb-booking-main">
					<div className="nb-form-card">
						<div className="nb-form-card__head">
							{ ! isDone && (
								<StepIndicator
									steps={ stepLabels }
									current={ step }
								/>
							) }
							<div className="nb-form-card__title">
								{ currentKey === 'time' &&
									__(
										'Pick a date & time',
										'nemtly-booking'
									) }
								{ currentKey === 'details' &&
									__( 'Your details', 'nemtly-booking' ) }
								{ currentKey === 'payment' &&
									__( 'Payment', 'nemtly-booking' ) }
								{ currentKey === 'done' &&
									__(
										'Booking confirmed',
										'nemtly-booking'
									) }
							</div>
						</div>

						<div
							className="nb-form-card__body nb-step-panel"
							ref={ panelRef }
						>
							{ currentKey === 'time' && (
								<div className="nb-cal-wrap">
									<div>
										<div className="nb-section-title">
											{ __(
												'Select a date',
												'nemtly-booking'
											) }
										</div>
										<DatePicker
											availableDates={ bookableDates }
											selectedDate={ selectedDate }
											onDateSelect={ ( d ) => {
												setSelectedDate( d );
												setSelectedSlot( null );
											} }
											loading={ datesLoading }
										/>
									</div>
									<div>
										<div className="nb-section-title">
											{ selectedDate
												? __(
														'Available times',
														'nemtly-booking'
												  )
												: __(
														'Pick a date first',
														'nemtly-booking'
												  ) }
										</div>
										{ selectedDate ? (
											<>
												{ event.timezone && (
													<div className="nb-slot-tz">
														{ event.timezone }
													</div>
												) }
												<SlotPicker
													slots={ slots }
													selectedSlot={
														selectedSlot
													}
													onSlotSelect={
														setSelectedSlot
													}
													showCapacity={
														event.event_type ===
														'group'
													}
													loading={ slotsLoading }
												/>
											</>
										) : (
											<div className="nb-slot-empty nb-slot-empty--hint">
												<div
													className="nb-slot-empty__icon"
													aria-hidden="true"
												>
													📅
												</div>
												{ __(
													'Select a highlighted date to see available times.',
													'nemtly-booking'
												) }
											</div>
										) }
									</div>
								</div>
							) }

							{ currentKey === 'details' && (
								<>
									<AttendeeForm
										form={ form }
										errors={ errors }
										onChange={ ( key, value ) => {
											setForm( ( f ) => ( {
												...f,
												[ key ]: value,
											} ) );
											if ( errors[ key ] ) {
												setErrors( ( prev ) => ( {
													...prev,
													[ key ]: '',
												} ) );
											}
										} }
										disabled={ isLoading }
									/>

									{ showPayMethodPicker && (
										<div
											className="nb-pay-methods"
											role="radiogroup"
											aria-label={ __(
												'Payment method',
												'nemtly-booking'
											) }
										>
											<button
												type="button"
												className={ `nb-pay-method${
													paymentMethod === 'stripe'
														? ' nb-pay-method--selected'
														: ''
												}` }
												onClick={ () =>
													setPaymentMethod( 'stripe' )
												}
												role="radio"
												aria-checked={
													paymentMethod === 'stripe'
												}
											>
												<span
													className="nb-pay-method__radio"
													aria-hidden="true"
												/>
												<span>
													<span className="nb-pay-method__name">
														💳{ ' ' }
														{ __(
															'Card',
															'nemtly-booking'
														) }
													</span>
													<span className="nb-pay-method__sub">
														{ __(
															'Visa, Mastercard, Amex',
															'nemtly-booking'
														) }
													</span>
												</span>
											</button>
											<button
												type="button"
												className={ `nb-pay-method${
													paymentMethod === 'manual'
														? ' nb-pay-method--selected'
														: ''
												}` }
												onClick={ () =>
													setPaymentMethod( 'manual' )
												}
												role="radio"
												aria-checked={
													paymentMethod === 'manual'
												}
											>
												<span
													className="nb-pay-method__radio"
													aria-hidden="true"
												/>
												<span>
													<span className="nb-pay-method__name">
														{ paymentMethods.manual_payment_label ||
															__(
																'Bank transfer',
																'nemtly-booking'
															) }
													</span>
													<span className="nb-pay-method__sub">
														{ __(
															'Pay offline',
															'nemtly-booking'
														) }
													</span>
												</span>
											</button>
										</div>
									) }

									{ errors.submit && (
										<div
											className="nb-field__error"
											role="alert"
										>
											{ errors.submit }
										</div>
									) }
								</>
							) }

							{ currentKey === 'payment' && (
								<>
									<div className="nb-order-summary">
										<div className="nb-order-summary__label">
											{ __(
												'Order summary',
												'nemtly-booking'
											) }
										</div>
										<div className="nb-order-summary__line">
											<span>
												{ event.title?.rendered ||
													event.title }
											</span>
											<span>
												{ formatMoney(
													symbol,
													event.price || 0
												) }
											</span>
										</div>
										{ ( orderDate || orderTime ) && (
											<div className="nb-order-summary__meta">
												{ orderDate }
												{ orderTime
													? ` · ${ orderTime }`
													: '' }
											</div>
										) }
										<div className="nb-order-summary__total">
											<span>
												{ __(
													'Total',
													'nemtly-booking'
												) }
											</span>
											<span>
												{ formatMoney(
													symbol,
													event.price || 0
												) }
											</span>
										</div>
									</div>

									{ clientSecret && stripePromise && (
										<Elements
											stripe={ stripePromise }
											options={ { clientSecret } }
										>
											<PaymentStep
												registerConfirm={
													registerConfirm
												}
												onProcessing={ setIsLoading }
												onPaid={ handlePaid }
												onError={ ( msg ) =>
													setErrors( { submit: msg } )
												}
											/>
										</Elements>
									) }

									{ errors.submit && (
										<div
											className="nb-field__error"
											role="alert"
										>
											{ errors.submit }
										</div>
									) }
								</>
							) }

							{ currentKey === 'done' && (
								<Confirmation
									event={ event }
									slotBased={ slotBased }
									selectedDate={ selectedDate }
									selectedSlot={ selectedSlot }
									attendeeName={ attendeeName }
									attendeeEmail={ form.email }
									bookingRef={
										bookingResult && bookingResult.uuid
									}
									variant={ successVariant }
									manualInstructions={ manualInstructions }
								/>
							) }

							{ /**
							 * Filters extra content appended to the current
							 * step panel. Returns `null` by default; also the
							 * only content rendered for custom step keys.
							 *
							 * @param {?Element} content `null` by default.
							 * @param {Object}   ctx     Booking context, plus `currentKey`.
							 */ }
							{ applyFilters(
								'nemtly_booking.booking.step_render',
								null,
								{ ...hookContext, currentKey }
							) }
						</div>

						{ ! isDone && (
							<div className="nb-form-card__foot">
								<div>
									{ step > 0 && (
										<button
											type="button"
											className="nemtly-button-ghost"
											onClick={ handleBack }
											disabled={ isLoading }
										>
											{ __( '← Back', 'nemtly-booking' ) }
										</button>
									) }
								</div>
								<button
									type="button"
									className="nemtly-button-primary"
									onClick={ handleNext }
									disabled={ nextDisabled }
								>
									{ isLoading && (
										<span
											className="nb-spinner"
											aria-hidden="true"
										/>
									) }
									{ nextLabel }
								</button>
							</div>
						) }

						{ isDone && (
							<div className="nb-form-card__foot nb-form-card__foot--center">
								<button
									type="button"
									className="nemtly-button-ghost"
									onClick={ resetAll }
								>
									{ showEntry
										? __(
												'← Back to event',
												'nemtly-booking'
										  )
										: __(
												'Make another booking',
												'nemtly-booking'
										  ) }
								</button>
							</div>
						) }
					</div>
				</div>

				{ ! isDone && (
					<aside className="nb-booking-sidebar">
						<BookingSummary
							event={ event }
							slotBased={ slotBased }
							selectedDate={ selectedDate }
							selectedSlot={ selectedSlot }
							attendeeName={ attendeeName }
						/>
					</aside>
				) }
			</div>
		</div>
	);
};

export default BookingForm;
